import {
  Home,
  Settings,
  Users,
  Calendar,
  FileCheck,
  FileUser,
  FolderOpen,
  BookOpen,
  Globe,
  Plug,
  UserCog,
  History,
  Key,
  Package,
  ShieldCheck,
  EyeOff,
  Briefcase,
  Building2,
  Sparkles,
  MessageCircle,
  MapPin,
  Mail,
  MailCheck,
  AtSign,
  Inbox,
  PenLine,
  MessagesSquare,
  Video,
  ClipboardList,
  GitBranch,
  Workflow,
  Archive,
  Megaphone,
  BookText,
  HelpCircle,
  FileBarChart,
  Contact,
  Gift,
  Network,
  LayoutTemplate,
} from "lucide-react";
import type { NavSection } from "@skypointcloud/skypoint-ui";
import { isAdminRole, isNavigationAvailable } from "../../../../shared/roles";
import type { DataScope } from "../../../../shared/types/accessScope";
import { dashboardRoleFor } from "../../../../shared/types/dashboard";
import {
  SKY_NAV_IDS,
  SKY_SURFACE_LABELS,
  skyNavSurfacesFor,
  type SkySurfaceProduct,
} from "../../skyagent/surfaces";
import {
  PERF_NAV_IDS,
  buildPerformanceNavSections,
  type PerformanceNavContext,
} from "../../../products/performance/nav";
import type { ProductKey } from "../../../../shared/products/keys";
import { ROUTES } from "../../../components/shared/constant";
export { PERF_NAV_IDS } from "../../../products/performance/nav";
// skyPerformance IT's sidebar (spec 2026-10-26 §4.6), the same way: this file is the one hire-shell
// place that names a product's nav.
import { PIT_NAV_IDS, buildPerformanceItNavSections } from "../../../products/performance-it/nav";
export { PIT_NAV_IDS } from "../../../products/performance-it/nav";
// Re-exported, not used here: sidebarConfig stays a plain module; sidebar.tsx and AppLayout call it.
export { usePerformanceNavContext } from "../../../products/performance/hooks/usePerformanceNavContext";

export const NAV_IDS = {
  DASHBOARD: "dashboard",
  // Employee referral hub. These four are the entire sidebar for the `employee`
  // role and appear for no other role.
  REFERRAL_HUB: "referral-hub",
  REFERRAL_OPEN_ROLES: "referral-open-roles",
  MY_REFERRALS: "my-referrals",
  REFERRAL_REWARDS: "referral-rewards",
  APPLICANTS: "applicants",
  CANDIDATE_INBOX: "candidate-inbox",
  BULK_UPLOADS: "bulk-uploads",
  JOBS: "jobs",
  SCHEDULED_TOURS: "scheduled-tours",
  SURVEYS: "surveys",
  CAREER_PORTALS: "career-portals",
  // The skyAgent + skyBrain ids are spread in from the surface catalog rather
  // than restated here, so routeMapping's Record<NavId, …> maps stop compiling
  // the moment a surface is added there without being routed.
  ...SKY_NAV_IDS,
  SETTINGS: "settings",
  CANDIDATE_ENGAGEMENT: "candidate-engagement",
  GENERAL: "general",
  LOCATIONS: "locations",
  DEPARTMENTS: "departments",
  USERS: "users",
  ROLES: "roles",
  ACCESS_MANAGEMENT: "access-management",
  ROLE_MANAGEMENT: "role-management",
  CLIENTS_MANAGEMENT: "clients-management",
  LOGS: "logs",
  PLATFORM: "platform",
  // Skypoint staff only (isPlatformAdmin): an instance's products (Task 18.b).
  INSTANCE_PRODUCTS: "instance-products",
  TENANTS: "tenants",
  INTEGRATIONS: "integrations",
  EMAIL_TEMPLATES: "email-templates",
  SMS_TEMPLATES: "sms-templates",
  EMAIL_SIGNATURE: "email-signature",
  SENDER_EMAILS: "sender-emails",
  FEEDBACK_TEMPLATES: "feedback-templates",
  AI_INTERVIEW_TEMPLATES: "ai-interview-templates",
  INTERVIEW_GUIDES: "interview-guides",
  PIPELINE: "pipeline",
  AI_SCORING: "ai-scoring",
  ARCHIVE_REASONS: "archive-reasons",
  JOB_POSTING_OPTIONS: "job-posting-options",
  JOB_TITLES: "job-titles",
  HELP_PLUS_SUPPORT: "help-plus-support",
  CANDIDATE_CONTACT_INFO: "candidate-contact-info",
  RESUME_DIGEST: "resume-digest",
  RESUME_TEMPLATES: "resume-templates",
  ANONYMIZATION_CONFIGS: "anonymization-configs",
  REPORTS: "reports",
  TALENT_REDISCOVERY: "talent-rediscovery",
  DOCUMENT_CENTRE: "document-centre",
  OFFER_TEMPLATES: "offer-templates",
  OFFER_WORKFLOWS: "offer-workflows",
  // The one Templates item: opens the tiled Templates page; its entries are the template pages.
  TEMPLATES: "templates",
  // skyPerformance's ids, spread in like SKY_NAV_IDS so routeMapping's Record<NavId, …> fails to
  // compile when a performance page is added without a route.
  ...PERF_NAV_IDS,
  // skyPerformance IT's ids, likewise.
  ...PIT_NAV_IDS,
} as const;

export type NavId = (typeof NAV_IDS)[keyof typeof NAV_IDS];

/**
 * Whose Inbox the server refuses whatever `inbox.view` says (server/routes/email.ts):
 * a client never reaches the mail threads (X-A22), an interviewer never gets the
 * Inbox (X-A24), and a role that sees no candidates has no threads to read.
 */
const NO_INBOX_SCOPES: ReadonlySet<DataScope> = new Set([
  "client_shares",
  "assigned_applications",
  "none",
]);

/** False only once the caller's data scope is known to be one the Inbox refuses. */
export function inboxAllowedFor(dataScope: DataScope | null | undefined): boolean {
  return !dataScope || !NO_INBOX_SCOPES.has(dataScope);
}

/**
 * False only once the caller's data scope is known to be one the Document
 * Centre refuses: the server turns away client-portal users and interviewers
 * outright (routes/candidateDocuments), and a role that sees no candidates has
 * nobody's documents to look at. The same three scopes the Inbox refuses.
 */
export function documentCentreAllowedFor(dataScope: DataScope | null | undefined): boolean {
  return !dataScope || !NO_INBOX_SCOPES.has(dataScope);
}

/**
 * Whether a dashboard exists for this caller at all.
 *
 * `NAVIGATION_REQUIRES` gates Dashboard on `applicants.view` — the permission
 * half of #806, and not the whole answer. `GET /dashboard/summary` shapes itself
 * from the role's DATA SCOPE, so `none` and `client_shares` get a 403
 * (`DASHBOARD_NOT_FOR_ROLE`) however many permissions are granted.
 *
 * That gap is reachable from Role Management: switching Dashboard on for
 * Employee grants `applicants.view` alongside it, which satisfied the nav gate
 * and drew a link straight to that 403 — "No dashboard for your role" on a page
 * the sidebar had just offered. Two dev tenants are in exactly that state.
 *
 * Asking `dashboardRoleFor` asks the server's own question with the server's own
 * function, so the sidebar and the route cannot drift apart.
 *
 * False only once the answer is known, like `inboxAllowedFor` above: an
 * unresolved scope or an empty permission map is a transient miss, not a
 * refusal, and `permissionAllows` tolerates it for the same reason — blanking
 * someone's navigation over a failed lookup is the worse failure.
 */
export function dashboardAllowedFor(
  dataScope: DataScope | null | undefined,
  permissions: Record<string, boolean> | null | undefined,
): boolean {
  if (!dataScope) return true;
  if (!permissions || Object.keys(permissions).length === 0) return true;
  return dashboardRoleFor({ dataScope, permissions }) !== null;
}

interface PermissionFlags {
  isFullAccess: boolean;
  isTenantAdmin: boolean;
  isPlatformAdmin: boolean;
  isInstanceAdmin: boolean;
  rbacNavigation?: Record<string, boolean>;
  rbacPermissions?: Record<string, boolean>;
  /**
   * The user's resolved role name for the current instance (e.g. "admin",
   * "team_member", "interviewer"). Comes from rbacApi's getMyPermissions response.
   * Used for fine-grained role gating that goes beyond the broader admin flags.
   */
  userRoleName?: string | null;
  instanceName?: string;
  instanceId?: string | null;
  inboxUnreadCount?: number;
  /** Candidates whose submitted documents nobody has opened: the Document Centre's badge. */
  newDocumentSubmissions?: number;
  /**
   * The caller's data scope (GET /me/access-scope). Unknown while it loads, which
   * leaves the Inbox to its switches rather than bouncing someone off /inbox.
   */
  dataScope?: DataScope | null;
  /**
   * Whether this instance may use SkyAgent, as resolved by the server from
   * `public.skyagent_enabled_instances`. Passed in rather than derived here:
   * this is a plain function, not a component, so it cannot read the store, and
   * the caller already holds the value.
   */
  isSkyAgentEnabled?: boolean;
  /**
   * Whether this instance has SharePoint connected (GET /sharepoint/status:
   * `configured && org.connected`). Passed in for the same reason as the flag
   * above. False while the status loads or when it fails: the Document Centre
   * entry appearing a beat late beats one that flashes in for an instance that
   * never set it up.
   */
  isSharePointConnected?: boolean;
}

export interface ProductNavContext {
  activeProduct: ProductKey;
  enabledProducts: readonly ProductKey[];
  performance?: PerformanceNavContext;
  /** skyPerformance IT's: whether the caller holds an IT role here. */
  performanceIt?: { held: boolean };
}

/**
 * Whether the shell needs skyPerformance's nav context (its config and scope reads) for `product`.
 * skyPerformance only: on an IT page none of skyPerformance's reads are made. Here, so this file
 * stays the one client place that names the performance key (spec §2).
 */
export function needsPerformanceNavContext(product: ProductKey): boolean {
  return product === "skyperformance";
}

/**
 * The non-default product whose users permission also opens the shared Users page (spec §5: "hire
 * users.view or perf_hr"), when the instance has it; else null. Kept here so this file stays the
 * one client place that names the performance key (spec §2).
 */
export function sharedUsersProductOf(enabledProducts: readonly ProductKey[]): ProductKey | null {
  return enabledProducts.includes("skyperformance") ? "skyperformance" : null;
}

/**
 * The Users page both products share (`/settings/access-management` and below), for the ACCESS
 * rules. Which shell it is drawn in is SHARED_PATHS' business (routes/sharedPaths.ts).
 */
export function isSharedUsersPath(pathname: string): boolean {
  return (
    pathname === ROUTES.ACCESS_MANAGEMENT || pathname.startsWith(ROUTES.ACCESS_MANAGEMENT + "/")
  );
}

/** The staff Products page (Task 18.b): product-neutral, like the shared Users page. */
export function isInstanceProductsPath(pathname: string): boolean {
  return (
    pathname === ROUTES.INSTANCE_PRODUCTS || pathname.startsWith(ROUTES.INSTANCE_PRODUCTS + "/")
  );
}

/** The staff Products entry, drawn for a platform admin only. */
const INSTANCE_PRODUCTS_ITEM = { id: NAV_IDS.INSTANCE_PRODUCTS, label: "Products", icon: Package };

/**
 * The performance sidebar, plus the staff Products entry for a platform admin (Task 18.b): an entry
 * of the one Settings item (D5, 2026-10-08 shell spec), which is added, last in the first section,
 * when the admin has no other entry, so it opens the tiled Settings page with the Products tile
 * alone. Anyone else gets the sections untouched.
 */
function withStaffProducts(sections: NavSection[], isPlatformAdmin: boolean): NavSection[] {
  if (!isPlatformAdmin) return sections;
  const hub = sections.flatMap((s) => s.items).find((i) => i.id === PERF_NAV_IDS.PERF_SETTINGS);
  if (hub) {
    return sections.map((s) => ({
      ...s,
      items: s.items.map((i) =>
        i !== hub ? i : { ...i, children: [...(i.children ?? []), INSTANCE_PRODUCTS_ITEM] },
      ),
    }));
  }
  const settings = {
    id: PERF_NAV_IDS.PERF_SETTINGS,
    label: "Settings",
    icon: Settings,
    children: [INSTANCE_PRODUCTS_ITEM],
  };
  if (sections.length === 0) return [{ header: "Performance", items: [settings] }];
  return sections.map((s, index) => (index === 0 ? { ...s, items: [...s.items, settings] } : s));
}

/**
 * Whether the route guard admits the shared Users page (`/settings/access-management`), as
 * AppLayout's useRouteGuard decides it (routeGuardRules' routeAllowed): it is a hire route, so it is
 * decided on the HIRE sections, admitted by the performance users permission on an instance with
 * performance (sharedUsersRouteAccess), else by a drawn entry whose route pattern holds it (Users,
 * Roles or Users in the Settings group: allowedBasesOf). Restated here because routeGuardRules
 * imports this module; settingsHubTiles.test pins it equal to routeAllowed for every fixture.
 */
export function sharedUsersAdmitted(
  hireSections: readonly NavSection[],
  managesProductUsers: boolean,
): boolean {
  if (managesProductUsers) return true;
  const owners = new Set<string>([NAV_IDS.ACCESS_MANAGEMENT, NAV_IDS.USERS, NAV_IDS.ROLES]);
  return hireSections.some((section) =>
    section.items.some(
      (item) => owners.has(item.id) || (item.children ?? []).some((child) => owners.has(child.id)),
    ),
  );
}

/** The sections without one Settings entry; the Settings item goes too when it is left with none. */
function withoutEntry(sections: NavSection[], entryId: string): NavSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.flatMap((item) => {
      if (item.id !== PERF_NAV_IDS.PERF_SETTINGS) return [item];
      const children = (item.children ?? []).filter((child) => child.id !== entryId);
      return children.length > 0 ? [{ ...item, children }] : [];
    }),
  }));
}

export function buildNavSections(
  permissions: PermissionFlags,
  /**
   * Candidate Engagement is opt-in per instance. Defaults to false so the entry
   * stays hidden while the flag is still loading, and for any caller that does
   * not pass it — a nav item that flashes in and out on every page load is
   * worse than one that appears a beat late.
   */
  engagementEnabled = false,
  /**
   * Which product's sidebar to build (spec §6). Absent, or hire: exactly today's sections, pinned
   * by sidebarConfig.snapshot.test.ts. This is the ONE place in the client that compares against
   * the performance key (spec §2).
   */
  products?: ProductNavContext,
): NavSection[] {
  if (products?.activeProduct === "skyperformance_it") {
    // Fail closed, as skyPerformance: IT's sidebar only where the instance has it and the caller
    // holds an IT role; anything else draws nothing, so the guard admits no IT page.
    if (!products.enabledProducts.includes(products.activeProduct)) return [];
    // No Settings item yet, so no staff Products entry either: staff reach it from hire.
    return buildPerformanceItNavSections({ held: products.performanceIt?.held === true });
  }
  if (products?.activeProduct === "skyperformance") {
    // Fail closed (a UI hint only; the server gates every performance route itself). A
    // performance URL on an instance without the product builds the hire sidebar, which admits no
    // /performance route, so the guard sends the user to the hire landing. With the product but no
    // context loaded there is nothing to draw: no performance link is guessed.
    if (products.enabledProducts.includes(products.activeProduct)) {
      if (!products.performance) return [];
      const sections = withStaffProducts(
        buildPerformanceNavSections(products.performance),
        permissions.isPlatformAdmin,
      );
      // P2-1 (2026-10-08 shell spec, D5: a tile never shows a page the guard refuses). The Users
      // entry stays only when the guard admits `/settings/access-management` to this person.
      return sharedUsersAdmitted(
        buildNavSections(permissions, engagementEnabled),
        products.performance.managesPerformanceUsers === true &&
          sharedUsersProductOf(products.enabledProducts) !== null,
      )
        ? sections
        : withoutEntry(sections, NAV_IDS.ACCESS_MANAGEMENT);
    }
  }
  const {
    isTenantAdmin,
    isPlatformAdmin,
    isInstanceAdmin,
    rbacNavigation,
    rbacPermissions,
    userRoleName,
    instanceName,
    instanceId,
    inboxUnreadCount,
    newDocumentSubmissions,
    dataScope,
    isSkyAgentEnabled = false,
    isSharePointConnected = false,
  } = permissions;
  const isAdmin = isTenantAdmin || isPlatformAdmin;
  // Resolved from the ROLE, at whatever scope it was granted — an instance's
  // admin is an admin of that instance. isPlatformAdmin stays as a fall-back for
  // SkyPoint staff whose role resolves before an instance is selected.
  const isAdminTier = isPlatformAdmin || isAdminRole(userRoleName);

  /**
   * Whether the caller's stored navigation map has this entry switched on.
   *
   * Raw, and admin-blind — unlike `shouldShow` below, which admits an admin to
   * everything. Sections that belong to one role and to no other read this
   * instead, so turning them on for that role does not also add them to every
   * administrator's sidebar.
   */
  const navFlag = (navKey: string): boolean => rbacNavigation?.[navKey] === true;

  /**
   * Whether the permission behind a sidebar entry is held.
   *
   * A nav flag decides whether a link is DRAWN; the permission decides whether
   * the page it points at will open. Switching Dashboard on for the Client role
   * drew the link and then bounced the click, which is the second half of #806.
   *
   * An empty or absent permission map means resolution did not answer — on a
   * fresh sign-in, or a lookup that failed. Falling back to the nav flag alone
   * there is deliberate: the sidebar is not the access control (every router
   * behind it carries its own gate), and blanking someone's whole navigation
   * over a transient miss is a worse failure than drawing a link they will be
   * refused at.
   */
  const permissionAllows = (navKey: string): boolean => {
    if (!rbacPermissions || Object.keys(rbacPermissions).length === 0) return true;
    return isNavigationAvailable(navKey, rbacPermissions);
  };

  const shouldShow = (navKey: string): boolean => {
    if (isAdmin) return true;
    if (rbacNavigation) {
      return rbacNavigation[navKey] === true && permissionAllows(navKey);
    }
    return false;
  };

  if (!isAdmin && !rbacNavigation) {
    return [];
  }

  const sections: NavSection[] = [];

  // The employee referral hub — the whole sidebar for the `employee` role as it
  // ships, and available to any role an admin switches it on for.
  //
  // It used to be an early return on `userRoleName === "employee"`, which is why
  // #806 reports that an Employee's configuration saves and then does nothing:
  // the branch returned these items and stopped, so every other Sidebar
  // Visibility switch on that role was read by nothing. Configuration decides
  // now, and the function carries on to the rest of the sidebar — an employee
  // with nothing else switched on still gets exactly these items, because every
  // other flag on the role is false.
  //
  // Read through `navFlag`, not `shouldShow`. `shouldShow` admits an admin to
  // every key, so flag-only gating would put this portal in every
  // administrator's sidebar; HR's view of the programme is a separate admin
  // surface, not this one.
  if (navFlag("showReferralHub")) {
    const referralItems: NavSection["items"] = [
      // "Dashboard": it is the Employee's home, and calling it Overview beside a Dashboard
      // switch in Role Management read as two different pages, one of them missing.
      { id: NAV_IDS.REFERRAL_HUB, label: "Dashboard", icon: Home },
      { id: NAV_IDS.REFERRAL_OPEN_ROLES, label: "Open Roles", icon: Briefcase },
      { id: NAV_IDS.MY_REFERRALS, label: "My Referrals", icon: Users },
    ];
    if (navFlag("showRewards")) {
      referralItems.push({ id: NAV_IDS.REFERRAL_REWARDS, label: "Rewards", icon: Gift });
    }
    sections.push({ header: "Referrals", items: referralItems });
  }

  // The same shape as the Inbox two sections below: a nav switch cannot conjure
  // a page the server shapes from the data scope. An admin keeps the entry
  // whatever their scope resolution says.
  if (shouldShow("showDashboard") && (isAdmin || dashboardAllowedFor(dataScope, rbacPermissions))) {
    sections.push({
      header: "Home",
      items: [
        {
          id: NAV_IDS.DASHBOARD,
          label: "Dashboard",
          icon: Home,
        },
      ],
    });
  }

  const hireItems = [];

  if (shouldShow("showLeads")) {
    hireItems.push({
      id: NAV_IDS.APPLICANTS,
      label: "Applicants",
      icon: Users,
    });
  }

  // Inbox surfaces cross-applicant email/SMS threads (incl. salary + offer
  // context in subject lines). That is why it was admin-only — but hardcoding
  // it here meant the `inbox.view` permission and its Communication group
  // switch decided nothing at all. `showInbox` + `inbox.view` make it the
  // customer's call, and both default off for every role below admin, so this
  // widens nobody's access on its own. The server still refuses some data scopes
  // whatever the switches say (inboxAllowedFor).
  if (shouldShow("showInbox") && inboxAllowedFor(dataScope)) {
    hireItems.push({
      id: NAV_IDS.CANDIDATE_INBOX,
      label: "Inbox",
      icon: Inbox,
      badge:
        inboxUnreadCount && inboxUnreadCount > 0
          ? inboxUnreadCount > 99
            ? "99+"
            : inboxUnreadCount
          : undefined,
    });
  }

  if (shouldShow("showJobs")) {
    hireItems.push({
      id: NAV_IDS.JOBS,
      label: "Jobs",
      icon: Briefcase,
    });
  }

  if (shouldShow("showTours")) {
    hireItems.push({
      id: NAV_IDS.SCHEDULED_TOURS,
      label: "Scheduled Interviews",
      icon: Calendar,
    });
  }

  // Gated on showLeads: the Document Centre lists the same candidates as
  // Applicants, so whoever is shown one is shown the other, once the
  // organisation has connected SharePoint. An instance that never set it up
  // gets no menu item for it; admins find it under Settings, Integrations.
  // What is inside a folder is SharePoint's call, per person, and is not
  // decided here.
  if (isSharePointConnected && shouldShow("showLeads") && documentCentreAllowedFor(dataScope)) {
    hireItems.push({
      id: NAV_IDS.DOCUMENT_CENTRE,
      label: "Document Centre",
      icon: FolderOpen,
      // As the Inbox's badge is: a number, "99+" past that, nothing at zero.
      badge:
        newDocumentSubmissions && newDocumentSubmissions > 0
          ? newDocumentSubmissions > 99
            ? "99+"
            : newDocumentSubmissions
          : undefined,
    });
  }

  if (isAdmin || isInstanceAdmin) {
    hireItems.push({
      id: NAV_IDS.TALENT_REDISCOVERY,
      label: "Talent Rediscovery",
      icon: Sparkles,
    });
  }

  // Reports had no nav key at all, so this read `isAdmin` — which is #807: Team
  // Member and Limited Team Member both hold `reports.view`, the reports router
  // is gated on that key and nothing else, and there was no way to put the page
  // in their sidebar. Both roles ship with `showReports` on, matching the
  // permission they already had.
  if (shouldShow("showReports")) {
    hireItems.push({
      id: NAV_IDS.REPORTS,
      label: "Reports",
      icon: FileBarChart,
    });
  }

  if (hireItems.length > 0) {
    sections.push({
      header: "Hire",
      items: hireItems,
    });
  }

  // skyAgent and skyBrain are two top-level sections over one engine, matching
  // skyCommand's left nav: skyAgent is where work gets done, skyBrain is what
  // the agent knows. Both are drawn from the surface catalog, so a section here
  // can never disagree with the routes or the page headers.
  //
  // One gate covers both, and it is the gate this section has always had:
  // admin, plus the instance's SkyAgent entitlement (skyAgentGating.ts). The
  // split is a reorganisation of pages that were already behind it, not a new
  // capability, so nothing here widens access. The sidebar is not the access
  // control either way — every route behind it carries its own guard, and the
  // server refuses whatever the nav happens to draw.
  if (isAdmin && isSkyAgentEnabled) {
    for (const product of ["skyagent", "skybrain"] as SkySurfaceProduct[]) {
      sections.push({
        header: SKY_SURFACE_LABELS[product],
        items: skyNavSurfacesFor(product).map((surface) => ({
          id: surface.navId,
          label: surface.label,
          icon: surface.icon,
        })),
      });
    }
  }

  if (isAdmin || isInstanceAdmin) {
    // Named for what it holds: the AI scoring rubric, approval workflows, sender addresses and
    // career portals as entries of their own, and the templates and forms behind one Templates
    // item. It replaces a top-level "Templates" section that sat at the same level as Applicants
    // and Jobs, and avoids "Configuration", which SkyAgent already uses for its own nav item.
    //
    // The Templates item works as Settings does (settingsHub.ts): its children are its entries,
    // gated here and nowhere else. The drawer draws it as one item opening the tiled Templates page
    // (ROUTES.TEMPLATES_HUB), the route guard admits each entry's route as before, the page shows a
    // card per entry, and the item stays lit on every template page.
    sections.push({
      header: "Templates & Setup",
      items: [
        // First in the section. Moved from Hire with the gate it had there (admin or instance admin), the same as this
        // section's.
        {
          id: NAV_IDS.CAREER_PORTALS,
          label: "Career Portals",
          icon: Globe,
        },
        {
          id: NAV_IDS.AI_SCORING,
          label: "AI & Scoring",
          icon: Sparkles,
        },
        {
          id: NAV_IDS.OFFER_WORKFLOWS,
          label: "Approval Workflows",
          icon: Workflow,
        },
        {
          id: NAV_IDS.SENDER_EMAILS,
          label: "Sender Emails",
          icon: AtSign,
        },
        {
          id: NAV_IDS.TEMPLATES,
          label: "Templates",
          icon: LayoutTemplate,
          children: [
            {
              // Email and SMS templates are one screen with the type chosen from the page
              // heading, so they share a single entry. /templates/sms still resolves for anyone
              // holding an old link.
              id: NAV_IDS.EMAIL_TEMPLATES,
              label: "Email & SMS Templates",
              icon: Mail,
            },
            {
              id: NAV_IDS.EMAIL_SIGNATURE,
              label: "Email Signature",
              icon: PenLine,
            },
            {
              id: NAV_IDS.FEEDBACK_TEMPLATES,
              label: "Feedback Templates",
              icon: MessagesSquare,
            },
            {
              id: NAV_IDS.AI_INTERVIEW_TEMPLATES,
              label: "AI Interview Templates",
              icon: Video,
            },
            {
              id: NAV_IDS.INTERVIEW_GUIDES,
              label: "Interview Guides",
              icon: ClipboardList,
            },
            {
              id: NAV_IDS.OFFER_TEMPLATES,
              label: "Offer Templates",
              icon: FileCheck,
            },
            // Was a Settings entry, drawn only with Settings itself (showSettings), so it keeps
            // that gate here: the same people see it, and the route guard admits the same people.
            ...(shouldShow("showSettings")
              ? [
                  {
                    id: NAV_IDS.RESUME_TEMPLATES,
                    label: "Resume Templates",
                    icon: FileUser,
                  },
                ]
              : []),
            {
              id: NAV_IDS.SURVEYS,
              label: "Application Questionnaire",
              icon: BookOpen,
            },
          ],
        },
      ],
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const settingsItems: any[] = [];

  if (shouldShow("showSettings")) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const settingsChildren: any[] = [];

    if (isAdmin || isInstanceAdmin) {
      settingsChildren.push({ id: NAV_IDS.LOCATIONS, label: "Locations", icon: MapPin });
      settingsChildren.push({ id: NAV_IDS.DEPARTMENTS, label: "Departments", icon: Network });
      settingsChildren.push({ id: NAV_IDS.PIPELINE, label: "Pipeline", icon: GitBranch });
      if (engagementEnabled) {
        settingsChildren.push({
          id: NAV_IDS.CANDIDATE_ENGAGEMENT,
          label: "Candidate Engagement",
          icon: MessageCircle,
        });
      }

      settingsChildren.push({
        id: NAV_IDS.ARCHIVE_REASONS,
        label: "Archive Reasons",
        icon: Archive,
      });
      // Mirrors the backend's `requireAdmin` on PUT /config and POST /send, so
      // nobody sees a menu they cannot save from.
      if (isAdminTier) {
        settingsChildren.push({
          id: NAV_IDS.RESUME_DIGEST,
          label: "Resume Digest",
          icon: MailCheck,
        });
      }
      settingsChildren.push({
        id: NAV_IDS.ANONYMIZATION_CONFIGS,
        label: "Anonymization",
        icon: EyeOff,
      });
      settingsChildren.push({
        id: NAV_IDS.JOB_POSTING_OPTIONS,
        label: "Job Posting",
        icon: Megaphone,
      });
      settingsChildren.push({
        id: NAV_IDS.JOB_TITLES,
        label: "Job Titles",
        icon: BookText,
      });
    }

    if (shouldShow("showUsers") || isAdmin || isInstanceAdmin) {
      settingsChildren.push({
        id: NAV_IDS.ACCESS_MANAGEMENT,
        label: "Users",
        icon: UserCog,
      });
      settingsChildren.push({
        id: NAV_IDS.ROLE_MANAGEMENT,
        label: "Role Management",
        icon: ShieldCheck,
      });
      // Every /clients route is admin-only on the server (requireAdmin), so the
      // page is offered to Admins only: to anyone else it would refuse its own
      // first request. The route guard follows the nav.
      if (isAdmin || isInstanceAdmin) {
        settingsChildren.push({
          id: NAV_IDS.CLIENTS_MANAGEMENT,
          label: "Clients",
          icon: Building2,
        });
      }
    }
    if (isAdmin) {
      settingsChildren.push({ id: NAV_IDS.LOGS, label: "Activity Logs", icon: History });
    }
    if (isAdmin || isInstanceAdmin) {
      settingsChildren.push({ id: NAV_IDS.PLATFORM, label: "Session", icon: Key });
    }
    // Skypoint staff only (server-derived isPlatformAdmin): an instance's products (Task 18.b).
    if (isPlatformAdmin) settingsChildren.push(INSTANCE_PRODUCTS_ITEM);
    if ((isAdmin || isInstanceAdmin) && rbacPermissions?.["integrations.manage"] !== false) {
      settingsChildren.push({ id: NAV_IDS.INTEGRATIONS, label: "Integrations", icon: Plug });
    }
    settingsChildren.push({
      id: NAV_IDS.CANDIDATE_CONTACT_INFO,
      label: "Candidate Communication Info",
      icon: Contact,
    });

    settingsItems.push({
      id: NAV_IDS.SETTINGS,
      label: "Settings",
      icon: Settings,
      children: settingsChildren,
    });
    // Help + Support is its own sidebar item beside Settings, not a tile on the Settings page:
    // someone looking for help should not have to open Settings to find it. Same gate as before.
    // After Settings, so Settings stays the section's first item (the landing page for a role
    // whose sidebar starts here: AppLayout, HomeRoute).
    settingsItems.push({
      id: NAV_IDS.HELP_PLUS_SUPPORT,
      label: "Help + Support",
      icon: HelpCircle,
    });
  }

  if (settingsItems.length > 0) {
    sections.push({
      header: "Settings",
      items: settingsItems,
    });
  }

  return sections;
}
